/**
 * Real onboarding submission - POSTs to the Partner Connect backend.
 *
 * The backend exposes `POST /onboarding/submit` which generates a submissionId
 * and fans out to Pipedrive + Email + SMS internally. We just map the
 * frontend OnboardingData shape to SubmitOnboardingDto and forward it.
 */

import i18n from 'shared/i18n/i18n';
import type { SupportedLanguage } from 'shared/i18n/i18n';
import { apiClient } from 'shared/lib/apiClient';
import { formatOpeningHoursByLang } from 'features/onboarding/utils/openingHours';
import type {
  BusinessQuestions,
  DocumentKey,
  Documents,
  FileMeta,
  OnboardingData,
} from 'features/onboarding/types/onboarding.types';

export interface IntegrationResult {
  ok: boolean;
  ref?: string;
  message?: string;
}

export interface SubmitOnboardingResult {
  ok: boolean;
  submissionId: string;
  status: 'Submitted';
  integrations: {
    pipedrive: IntegrationResult;
    email: IntegrationResult;
    sms: IntegrationResult;
  };
}

interface UploadedDocumentPayload {
  key: DocumentKey;
  name: string;
  type: string;
  size: number;
  url?: string;
}

const documentsToArray = (docs: Documents): UploadedDocumentPayload[] => {
  const entries: [DocumentKey, FileMeta | undefined][] = [
    ['osekCertificate', docs.osekCertificate],
    ['taxClearance', docs.taxClearance],
    ['bankConfirmation', docs.bankConfirmation],
    ['businessLogo', docs.businessLogo],
  ];
  return entries
    .filter((e): e is [DocumentKey, FileMeta] => !!e[1])
    .map(([key, file]) => ({
      key,
      name: file.name,
      type: file.type,
      size: file.size,
      url: file.url,
    }));
};

/**
 * Backend currently models `otherDeliveryCompanies` as a single free-text
 * string. The new UI captures a structured multi-select + optional "Other"
 * input, so we flatten the selection here into a comma-separated label list
 * (localised to the partner's chosen language) before sending it on the wire.
 * This keeps the contract stable while giving humans a readable value in
 * Pipedrive / email notifications.
 */
const serialiseDeliveryCompanies = (q: BusinessQuestions, lang: string): string => {
  if (!q.deliveryCompanies.length) return '';
  const labels = q.deliveryCompanies.map((c) =>
    c === 'other'
      ? q.otherDeliveryCompanies.trim()
      : i18n.t(`businessQuestions.deliveryCompanyOptions.${c}`, { lng: lang }),
  );
  return labels.filter(Boolean).join(', ');
};

export const submitOnboarding = async (
  data: OnboardingData,
): Promise<SubmitOnboardingResult> => {
  const { metadata, documents, finalDetails, ...sections } = data;
  const currentLanguage = i18n.language.split('-')[0];
  const activeLanguage: SupportedLanguage =
    currentLanguage === 'he' || currentLanguage === 'ar' || currentLanguage === 'en'
      ? currentLanguage
      : metadata.language;

  const businessQuestionsForWire = {
    ...sections.businessQuestions,
    otherDeliveryCompanies: serialiseDeliveryCompanies(
      sections.businessQuestions,
      activeLanguage,
    ),
  };

  // The backend, Pipedrive and email templates expect `openingHours` as a
  // single human-readable string AND on `businessInfo` (legacy wire shape).
  // Notes is added as a new top-level field. `finalDetails` itself isn't
  // forwarded — it's an internal/UI grouping only.
  const businessInfoForWire = {
    ...sections.businessInfo,
    openingHours: formatOpeningHoursByLang(
      finalDetails.openingHours,
      activeLanguage,
    ),
  };

  const payload = {
    ...sections,
    businessInfo: businessInfoForWire,
    businessQuestions: businessQuestionsForWire,
    notes: finalDetails.notes?.trim() || undefined,
    // The backend re-checks that this token was issued for the phone number
    // being submitted, so the SMS gate holds even if the wizard is bypassed.
    phoneVerificationToken: metadata.phoneVerificationToken,
    documents: documentsToArray(documents),
    metadata: {
      language: activeLanguage,
      currentStep: metadata.currentStep,
      createdAt: metadata.createdAt,
    },
  };

  const res = await apiClient.post<SubmitOnboardingResult>(
    '/onboarding/submit',
    payload,
  );
  return res.data;
};
